import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ps-medical-logistics/artifacts/1dt4h2l7_Gemini_Generated_Image_dpwsejdpwsejdpws.png";

export default function PortalLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/portal";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, navigate, from]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back", {
        description: `Signed in as ${res.user.email}`,
      });
      navigate(from, { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left — dark gated panel */}
      <div className="relative hidden lg:flex flex-col bg-[#0F172A] text-white p-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-90 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(700px 360px at 80% 10%, rgba(180,83,9,0.16), transparent 60%)," +
              "radial-gradient(600px 360px at 10% 90%, rgba(5,150,105,0.18), transparent 60%)",
          }}
        />
        <div className="relative">
          <Link
            to="/"
            className="inline-flex items-center gap-3 group"
            data-testid="login-brand-link"
          >
            <img
              src={LOGO_URL}
              alt="PS GLOBAL SERIES, LLC"
              className="h-10 w-10 object-contain bg-white/5 p-1 rounded-sm"
            />
            <div>
              <div className="font-display font-bold tracking-tight">
                PS GLOBAL SERIES, LLC
              </div>
              <div className="eyebrow text-slate-300 mt-1">
                Client Portal
              </div>
            </div>
          </Link>
        </div>

        <div className="relative mt-auto">
          <div className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-200">
            <Lock size={13} className="text-emerald-400" />
            Restricted Access
          </div>
          <h2 className="font-display mt-6 text-4xl font-light leading-[1.1] tracking-tight">
            A coordination workspace for{" "}
            <span className="font-semibold">contracted clients</span> only.
          </h2>
          <p className="mt-5 text-slate-300 max-w-md text-[15px] leading-relaxed">
            Supplier shortlists, negotiation memos and trade-liaison
            deliverables are gated behind authenticated access. If you have
            not been issued credentials, request a sourcing engagement from
            the public site.
          </p>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 w-fit"
          data-testid="login-back-link"
        >
          <ArrowLeft size={15} /> Back to site
        </Link>

        <div className="my-auto max-w-md w-full mx-auto py-12">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src={LOGO_URL}
              alt="PS GLOBAL SERIES, LLC"
              className="h-10 w-10 object-contain"
            />
            <div className="font-display font-bold tracking-tight text-slate-900">
              PS GLOBAL SERIES, LLC
            </div>
          </div>

          <div className="eyebrow">Sign In</div>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl font-light tracking-tight text-slate-900">
            Client Portal access.
          </h1>
          <p className="mt-3 text-slate-600 text-[15px]">
            Use the credentials issued by your PS GLOBAL trade-liaison
            advisor.
          </p>

          <form onSubmit={onSubmit} className="mt-9 space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label className="text-[12px] uppercase tracking-[0.15em] font-bold text-slate-600">
                Work email
              </Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                autoComplete="email"
                placeholder="you@company.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[12px] uppercase tracking-[0.15em] font-bold text-slate-600">
                Password
              </Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-sm"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || loading}
              data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3.5 text-sm font-semibold rounded-sm disabled:opacity-60 transition-colors"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              Enter Portal
            </button>

            <p className="text-xs text-slate-500 leading-relaxed">
              By signing in, you confirm that you are an authorized
              representative of a contracted corporate client and agree to
              our Terms of Service and B2B engagement policies.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
